// utils/rruleHelpers.ts
import * as RRuleLib from "rrule";
import moment from "moment";
import { RecurrenceData } from "./entities";

// Access RRule and Frequency from the namespace
const RRule = RRuleLib.RRule;
const Frequency = RRuleLib.Frequency;

/**
 * Converts moment day number (0=Sunday, 6=Saturday) to RRule weekday constant
 */
function momentDayToRRuleWeekday(day: number): any {
  const dayMap: { [key: number]: any } = {
    0: RRule.SU, // Sunday
    1: RRule.MO, // Monday
    2: RRule.TU, // Tuesday
    3: RRule.WE, // Wednesday
    4: RRule.TH, // Thursday
    5: RRule.FR, // Friday
    6: RRule.SA, // Saturday
  };
  return dayMap[day];
}

/**
 * Converts RRule weekday number (0=Monday, 6=Sunday) to moment day (0=Sunday).
 */
function rruleWeekdayToMomentDay(day: number): number {
  if (day === 6) return 0; // Sunday
  return day + 1; // Monday..Saturday
}

/**
 * Converts recurrence form data to an RFC 5545 compliant RRule string
 */
export function recurrenceToRRule(
  recurrence: RecurrenceData | null | undefined,
  startDate: moment.Moment,
): string | undefined {
  if (!recurrence || recurrence.type === "none") {
    return undefined;
  }

  const options: Partial<any> = {
    dtstart: startDate.toDate(),
  };

  // Set frequency based on recurrence type
  switch (recurrence.type) {
    case "daily":
      options.freq = Frequency.DAILY;
      options.interval = recurrence.interval || 1;
      break;

    case "weekly":
      options.freq = Frequency.WEEKLY;
      options.interval = recurrence.interval || 1;
      // Use daysOfWeek if provided (for weekly type), otherwise use startDate day
      // Note: weekdays field is for custom/weekdays types, not weekly
      if (recurrence.daysOfWeek && recurrence.daysOfWeek.length > 0) {
        options.byweekday = recurrence.daysOfWeek.map((day: number) =>
          momentDayToRRuleWeekday(day),
        );
      } else {
        options.byweekday = [momentDayToRRuleWeekday(startDate.day())];
      }
      break;

    case "biweekly":
      options.freq = Frequency.WEEKLY;
      options.interval = 2;
      // Default to startDate day for biweekly if not specified
      if (recurrence.daysOfWeek && recurrence.daysOfWeek.length > 0) {
        options.byweekday = recurrence.daysOfWeek.map((day: number) =>
          momentDayToRRuleWeekday(day),
        );
      } else {
        options.byweekday = [momentDayToRRuleWeekday(startDate.day())];
      }
      break;

    case "monthly-day":
    case "monthly":
      options.freq = Frequency.MONTHLY;
      options.interval = recurrence.interval || 1;
      // For monthly type, use dayOfMonth if provided
      // Ignore weekdays, daysOfWeek, unit, etc. that might be from other types
      if (recurrence.dayOfMonth !== undefined) {
        // By day of month
        options.bymonthday = recurrence.dayOfMonth;
      } else if (
        recurrence.weekOrdinal !== undefined &&
        recurrence.dayOfWeek !== undefined
      ) {
        // By weekday (e.g., 1st Friday) - for monthly-weekday type
        options.byweekday = [momentDayToRRuleWeekday(recurrence.dayOfWeek)];
        options.bysetpos = recurrence.weekOrdinal;
      } else {
        // Fallback to day of month from startDate
        options.bymonthday = startDate.date();
      }
      break;

    case "monthly-weekday": {
      options.freq = Frequency.MONTHLY;
      options.interval = recurrence.interval || 1;

      // Use weekOrdinal and dayOfWeek if provided, otherwise calculate from startDate
      if (
        recurrence.weekOrdinal !== undefined &&
        recurrence.dayOfWeek !== undefined
      ) {
        options.byweekday = [momentDayToRRuleWeekday(recurrence.dayOfWeek)];
        options.bysetpos = recurrence.weekOrdinal;
      } else {
        // Calculate which week of the month (1st, 2nd, 3rd, 4th, or -1 for last)
        const dayOfMonth = startDate.date();
        const weekOfMonth = Math.ceil(dayOfMonth / 7);
        const lastWeekOfMonth = Math.ceil(
          startDate.clone().endOf("month").date() / 7,
        );

        const setpos = weekOfMonth === lastWeekOfMonth ? -1 : weekOfMonth;
        options.byweekday = [momentDayToRRuleWeekday(startDate.day())];
        options.bysetpos = setpos;
      }
      break;
    }

    case "annually":
      options.freq = Frequency.YEARLY;
      options.interval = recurrence.interval || 1;
      // Use month and dayOfMonth if provided, otherwise use startDate
      options.bymonth = recurrence.month || startDate.month() + 1; // moment months are 0-indexed
      options.bymonthday = recurrence.dayOfMonth || startDate.date();
      break;

    case "weekdays":
      options.freq = Frequency.WEEKLY;
      options.interval = 1;
      // Always use weekdays (Mon-Fri) for weekdays type
      // Ignore any daysOfWeek or other fields that might be present
      options.byweekday = [RRule.MO, RRule.TU, RRule.WE, RRule.TH, RRule.FR];
      break;

    case "custom": {
      // Map custom unit to frequency
      const freqMap = {
        day: Frequency.DAILY,
        week: Frequency.WEEKLY,
        month: Frequency.MONTHLY,
        year: Frequency.YEARLY,
      };
      options.freq = freqMap[recurrence.unit || "week"];
      options.interval = recurrence.interval || 1;

      // For weekly custom, set specific weekdays
      if (recurrence.unit === "week") {
        if (recurrence.weekdays && recurrence.weekdays.length > 0) {
          const weekdayMap: { [key: string]: any } = {
            MO: RRule.MO,
            TU: RRule.TU,
            WE: RRule.WE,
            TH: RRule.TH,
            FR: RRule.FR,
            SA: RRule.SA,
            SU: RRule.SU,
          };
          options.byweekday = recurrence.weekdays.map((day) => weekdayMap[day]);
        } else {
          // Default to startDate's weekday if no weekdays specified
          options.byweekday = [momentDayToRRuleWeekday(startDate.day())];
        }
      }
      break;
    }

    default:
      // Unknown type - return undefined to prevent invalid RRULE
      console.warn(`Unknown recurrence type: ${recurrence.type}`);
      return undefined;
  }

  // Handle end conditions
  switch (recurrence.endType) {
    case "never":
      // No end date - recurrence continues indefinitely
      break;

    case "on":
      // Recurrence ends on a specific date
      if (recurrence.endDate) {
        options.until = moment(recurrence.endDate).toDate();
      }
      // If endDate is missing but endType is "on", recurrence continues indefinitely
      break;

    case "after":
      // Recurrence ends after a specific number of occurrences
      if (recurrence.occurrences && recurrence.occurrences > 0) {
        options.count = recurrence.occurrences;
      }
      // If occurrences is missing or 0, recurrence continues indefinitely
      break;

    default:
      // Unknown endType - default to never ending
      console.warn(`Unknown endType: ${recurrence.endType}`);
      break;
  }

  const rule = new RRule(options);
  return rule.toString();
}

/**
 * Parses an RRule string and converts it back to RecurrenceData
 * Useful for editing existing recurring events
 */
export function rruleToRecurrence(rruleString: string): RecurrenceData | null {
  try {
    const rule = RRule.fromString(rruleString);
    const options = rule.options;

    const recurrence: RecurrenceData = {
      type: "daily", // Default, will be overridden
      interval: options.interval || 1,
      endType: "never",
    };

    // Determine recurrence type
    if (options.freq === Frequency.DAILY && options.interval === 1) {
      recurrence.type = "daily";
    } else if (options.freq === Frequency.WEEKLY && options.interval === 1) {
      if (options.byweekday && options.byweekday.length === 5) {
        // Check if it's weekdays only (MO-FR)
        const weekdays = options.byweekday.map((wd: any) => {
          const day = typeof wd === "number" ? wd : wd.weekday;
          return day;
        });
        // Check if it's exactly MO, TU, WE, TH, FR
        const isWeekdays = weekdays.every((d: number) =>
          [0, 1, 2, 3, 4].includes(d),
        );
        recurrence.type = isWeekdays ? "weekdays" : "custom";
        if (!isWeekdays) {
          recurrence.unit = "week";
          const weekdayMap: { [key: number]: string } = {
            0: "MO",
            1: "TU",
            2: "WE",
            3: "TH",
            4: "FR",
            5: "SA",
            6: "SU",
          };
          recurrence.weekdays = weekdays.map(
            (d: number) => weekdayMap[d] || "MO",
          );
        }
      } else if (options.byweekday && options.byweekday.length === 1) {
        recurrence.type = "weekly";
      } else if (options.byweekday && options.byweekday.length > 1) {
        // Multiple weekdays - treat as custom
        recurrence.type = "custom";
        recurrence.unit = "week";
        const weekdayMap: { [key: number]: string } = {
          0: "MO",
          1: "TU",
          2: "WE",
          3: "TH",
          4: "FR",
          5: "SA",
          6: "SU",
        };
        recurrence.weekdays = options.byweekday.map((wd: any) => {
          const day = typeof wd === "number" ? wd : wd.weekday;
          return weekdayMap[day] || "MO";
        });
      } else {
        recurrence.type = "weekly";
      }
    } else if (options.freq === Frequency.MONTHLY && options.interval === 1) {
      const bymonthday = Array.isArray(options.bymonthday)
        ? options.bymonthday[0]
        : options.bymonthday;
      const bysetpos = Array.isArray(options.bysetpos)
        ? options.bysetpos[0]
        : options.bysetpos;
      const byweekday = Array.isArray(options.byweekday)
        ? options.byweekday[0]
        : options.byweekday;

      if (options.byweekday && bysetpos !== undefined && bysetpos !== null) {
        recurrence.type = "monthly-weekday";
        const weekday =
          typeof byweekday === "number"
            ? byweekday
            : (byweekday as any)?.weekday;
        if (weekday !== undefined && weekday !== null) {
          recurrence.dayOfWeek = rruleWeekdayToMomentDay(weekday);
        }
        recurrence.weekOrdinal = bysetpos;
      } else if (bymonthday !== undefined && bymonthday !== null) {
        recurrence.type = "monthly-day";
        recurrence.dayOfMonth = bymonthday;
      } else {
        recurrence.type = "monthly";
      }
    } else if (options.freq === Frequency.YEARLY && options.interval === 1) {
      recurrence.type = "annually";
    } else {
      // For intervals > 1, map to appropriate base type or custom
      if (options.freq === Frequency.DAILY) {
        // Daily with interval > 1 is custom
        recurrence.type = "custom";
        recurrence.unit = "day";
        recurrence.interval = options.interval;
      } else if (options.freq === Frequency.WEEKLY) {
        recurrence.interval = options.interval;
        if (options.byweekday && options.byweekday.length > 1) {
          // Multiple weekdays with interval > 1 - treat as custom
          recurrence.type = "custom";
          recurrence.unit = "week";
          const weekdayMap: { [key: number]: string } = {
            0: "MO",
            1: "TU",
            2: "WE",
            3: "TH",
            4: "FR",
            5: "SA",
            6: "SU",
          };
          recurrence.weekdays = options.byweekday.map((wd: any) => {
            const day = typeof wd === "number" ? wd : wd.weekday;
            return weekdayMap[day] || "MO";
          });
        } else {
          recurrence.type = options.interval === 2 ? "biweekly" : "weekly";
          if (options.byweekday) {
            const weekdayMap: { [key: number]: number } = {
              0: 0,
              1: 1,
              2: 2,
              3: 3,
              4: 4,
              5: 5,
              6: 6,
            };
            recurrence.daysOfWeek = options.byweekday.map((wd: any) => {
              const day = typeof wd === "number" ? wd : wd.weekday;
              return weekdayMap[day] ?? day;
            });
          }
        }
      } else if (options.freq === Frequency.MONTHLY) {
        recurrence.interval = options.interval;
        if (options.bymonthday) {
          // bymonthday can be a number or array, take first if array
          recurrence.dayOfMonth = Array.isArray(options.bymonthday)
            ? options.bymonthday[0]
            : options.bymonthday;
        }
        // If interval > 1 or has specific day, treat as custom monthly
        if (options.interval > 1 || options.bymonthday) {
          recurrence.type = "custom";
          recurrence.unit = "month";
        } else {
          recurrence.type = "monthly";
        }
      } else if (options.freq === Frequency.YEARLY) {
        recurrence.interval = options.interval;
        // If interval > 1, treat as custom yearly
        if (options.interval > 1) {
          recurrence.type = "custom";
          recurrence.unit = "year";
        } else {
          recurrence.type = "annually";
        }
      }
    }

    // Handle end conditions
    if (options.until) {
      recurrence.endType = "on";
      recurrence.endDate = moment(options.until);
    } else if (options.count) {
      recurrence.endType = "after";
      recurrence.occurrences = options.count;
    }

    return recurrence;
  } catch (error) {
    console.error("Failed to parse RRule:", error);
    return null;
  }
}

/**
 * Checks if an event is recurring
 */
export function isEventRecurring(event: {
  isRecurring?: boolean;
  recurrenceRule?: string;
  recurrence?: RecurrenceData | null;
}): boolean {
  return !!(
    event.isRecurring ||
    event.recurrenceRule ||
    (event.recurrence && event.recurrence.type !== "none")
  );
}

/**
 * Calculates the next occurrence date/time from an RRule string
 * Returns the next occurrence after the given date
 */
export function getNextOccurrence(
  rruleString: string,
  afterDate: moment.Moment,
): moment.Moment | null {
  try {
    const rule = RRule.fromString(rruleString);
    const nextDate = rule.after(afterDate.toDate(), true);
    return nextDate ? moment(nextDate) : null;
  } catch (error) {
    console.error("Failed to calculate next occurrence:", error);
    return null;
  }
}

/**
 * Formats recurrence schedule as human-readable text
 */
export function formatRecurrenceSchedule(event: {
  recurrence?: RecurrenceData | null;
  recurrenceRule?: string;
  dateRange?: { start?: moment.Moment };
}): string | null {
  let recurrence: RecurrenceData | null = null;

  // Try to get recurrence from event.recurrence first
  if (event.recurrence && event.recurrence.type !== "none") {
    recurrence = event.recurrence;
  } else if (event.recurrenceRule) {
    // Parse from RRule string
    recurrence = rruleToRecurrence(event.recurrenceRule);
  }

  if (!recurrence || recurrence.type === "none") {
    return null;
  }

  const interval = recurrence.interval || 1;
  const type = recurrence.type;

  switch (type) {
    case "daily":
      if (interval === 1) {
        return "Daily";
      }
      return `Every ${interval} days`;

    case "weekly":
      if (interval === 1) {
        if (recurrence.daysOfWeek && recurrence.daysOfWeek.length > 0) {
          const dayNames = [
            "Sunday",
            "Monday",
            "Tuesday",
            "Wednesday",
            "Thursday",
            "Friday",
            "Saturday",
          ];
          const days = recurrence.daysOfWeek.map((d) => dayNames[d]).join(", ");
          return `Weekly on ${days}`;
        }
        return "Weekly";
      }
      return `Every ${interval} weeks`;

    case "monthly-day":
      if (interval === 1) {
        if (recurrence.dayOfMonth) {
          return `Monthly on day ${recurrence.dayOfMonth}`;
        }
        return "Monthly";
      }
      return `Every ${interval} months`;

    case "monthly-weekday":
      if (interval === 1) {
        if (recurrence.weekOrdinal && recurrence.dayOfWeek !== undefined) {
          const ordinals = ["", "1st", "2nd", "3rd", "4th", "Last"];
          const dayNames = [
            "Sunday",
            "Monday",
            "Tuesday",
            "Wednesday",
            "Thursday",
            "Friday",
            "Saturday",
          ];
          const ordinal =
            recurrence.weekOrdinal === -1
              ? "Last"
              : ordinals[recurrence.weekOrdinal] ||
                `${recurrence.weekOrdinal}th`;
          const dayName = dayNames[recurrence.dayOfWeek] || "day";
          return `Monthly on the ${ordinal} ${dayName}`;
        }
        return "Monthly";
      }
      return `Every ${interval} months`;

    case "monthly":
      if (interval === 1) {
        return "Monthly";
      }
      return `Every ${interval} months`;

    case "annually":
      if (interval === 1) {
        if (recurrence.month && recurrence.dayOfMonth) {
          const monthNames = [
            "January",
            "February",
            "March",
            "April",
            "May",
            "June",
            "July",
            "August",
            "September",
            "October",
            "November",
            "December",
          ];
          return `Annually on ${monthNames[recurrence.month - 1]} ${recurrence.dayOfMonth}`;
        }
        return "Annually";
      }
      return `Every ${interval} years`;

    case "weekdays":
      return "Weekdays (Monday - Friday)";

    case "custom":
      if (recurrence.unit && recurrence.interval) {
        const unitNames = {
          day: "days",
          week: "weeks",
          month: "months",
          year: "years",
        };
        const unitName = unitNames[recurrence.unit] || recurrence.unit;
        if (
          recurrence.unit === "week" &&
          recurrence.weekdays &&
          recurrence.weekdays.length > 0
        ) {
          const weekdayLabels: { [key: string]: string } = {
            MO: "Monday",
            TU: "Tuesday",
            WE: "Wednesday",
            TH: "Thursday",
            FR: "Friday",
            SA: "Saturday",
            SU: "Sunday",
          };
          const days = recurrence.weekdays
            .map((d) => weekdayLabels[d] || d)
            .join(", ");
          return `Every ${interval} ${unitName} on ${days}`;
        }
        return `Every ${interval} ${unitName}`;
      }
      return "Custom schedule";

    default:
      return "Recurring event";
  }
}
